<template>
  <div v-if="slate" class="space-y-6">
    <!-- Action buttons -->
    <div class="flex items-center justify-end gap-3">
      <router-link to="/slates" class="btn-secondary">
        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
        </svg>
        Back
      </router-link>
      <button v-if="$checkPermission('slates.update')" @click="save" :disabled="saving" class="btn-primary">
        <svg v-if="!saving" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
        </svg>
        {{ saving ? 'Saving...' : 'Save' }}
      </button>
    </div>

    <!-- Row 1: Player + Details -->
    <div class="grid grid-cols-1 lg:grid-cols-[6fr_4fr] gap-6 items-stretch">
      <!-- Player -->
      <div class="bg-black rounded-xl overflow-hidden card-shadow relative min-h-[300px]">
        <VideoPlayer
          v-if="playerActive && slateReady"
          ref="player"
          :src="slateUrl"
          :type="slate.file_object?.content_type || 'video/mp4'"
          class="absolute inset-0 w-full h-full"
          @playing="isPlaying = true"
          @paused="isPlaying = false"
        />
        <div v-else class="absolute inset-0 flex flex-col items-center justify-center text-center">
          <button
            v-if="slateReady"
            @click="playerActive = true"
            class="w-16 h-16 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center transition-all hover:scale-110 group"
          >
            <svg class="w-7 h-7 text-white/70 group-hover:text-white ml-1 transition-colors" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
          </button>
          <template v-else>
            <svg class="w-12 h-12 text-gray-600 mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M17 7l-2-4H9L7 7"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/>
            </svg>
            <p class="text-sm text-gray-500">{{ slate.file_object?.status === 'uploading' ? 'Upload in progress' : 'Preview not available' }}</p>
          </template>
        </div>
      </div>

      <!-- Right column -->
      <div class="space-y-6">
        <!-- Details -->
        <div class="bg-white rounded-xl border border-gray-200/60 card-shadow">
          <div class="px-6 py-3 border-b border-gray-100">
            <h3 class="text-[15px] font-semibold text-gray-900">Details</h3>
          </div>
          <div class="p-6 space-y-5">
            <div>
              <label class="block text-sm font-medium text-gray-600 mb-1.5">ID</label>
              <div class="flex items-center w-full h-[42px] px-3.5 rounded-lg border border-gray-200 bg-gray-50">
                <span class="text-sm font-data text-gray-500 truncate flex-1">{{ slateId }}</span>
                <button @click="copyToClipboard(slateId, 'ID')" class="p-1 rounded text-gray-400 hover:text-[var(--color-primary)] transition-colors shrink-0 ml-2" title="Copy ID">
                  <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                </button>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-600 mb-1.5">Name</label>
              <input v-model="form.title" type="text" maxlength="200" placeholder="Slate name" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-600 mb-1.5">Slate URL</label>
              <div class="flex gap-2">
                <input :value="slateReady ? slateUrl : ''" type="text" disabled placeholder="Not available yet" />
                <button
                  v-if="slateReady"
                  @click="copyToClipboard(slateUrl)"
                  class="shrink-0 h-[42px] w-[42px] rounded-lg border border-gray-200 bg-white text-gray-400 hover:text-[var(--color-primary)] hover:border-[var(--color-primary)] flex items-center justify-center transition-colors"
                  title="Copy URL"
                >
                  <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- File info -->
        <div class="bg-white rounded-xl border border-gray-200/60 card-shadow">
          <div class="px-6 py-3 border-b border-gray-100">
            <h3 class="text-[15px] font-semibold text-gray-900">File Info</h3>
          </div>
          <div class="p-6 space-y-3">
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-400">Slate Status</span>
              <span :class="slateStatusClass">{{ slate.status || '—' }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-400">File Status</span>
              <span :class="fileStatusClass">{{ slate.file_object?.status || '—' }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-400">Size</span>
              <span class="text-sm font-data text-gray-700">{{ $filters.formatBytes(slate.file_object?.size || 0) }}</span>
            </div>
            <div v-if="slate.file_object?.content_type" class="flex items-center justify-between">
              <span class="text-sm text-gray-400">Type</span>
              <span class="text-sm font-data text-gray-700">{{ slate.file_object.content_type }}</span>
            </div>
            <div v-if="slate.created_at" class="flex items-center justify-between">
              <span class="text-sm text-gray-400">Created</span>
              <span class="text-sm font-data text-gray-700">{{ $filters.formatDatetime(slate.created_at) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete -->
    <DeleteResource
      v-if="$checkPermission('slates.delete')"
      warning-title="You are deleting this slate"
      warning-text="This will permanently remove the slate file. Slates in use by a livestream cannot be deleted."
      button-label="Delete Slate"
      confirm-label="I confirm this slate deletion"
      :confirm-message="`Delete '${slate.title || slate.file_object?.name}'? This cannot be undone.`"
      success-message="Slate deleted successfully"
      :endpoint="`/slates/${slateId}`"
      @deleted="$router.push('/slates')"
    />
  </div>
</template>

<script>
import DeleteResource from '../../components/DeleteResource.vue'
import VideoPlayer from '../../components/VideoPlayer.vue'
import api from '../../services/api.js'
import { notifySuccess, notifyError } from '../../services/notifications.js'

export default {
  name: 'SlateEdit',
  components: { DeleteResource, VideoPlayer },

  data() {
    return {
      slateId: null,
      slate: null,
      form: { title: '' },
      saving: false,
      playerActive: false,
      isPlaying: false
    }
  },

  computed: {
    slateReady() {
      return this.slate?.file_object?.status === 'success'
    },
    slateUrl() {
      return this.slateId ? this.$mediaUrl.slate(this.slateId) : ''
    },
    slateStatusClass() {
      const s = this.slate?.status
      if (s === 'success') return 'badge-live'
      if (s === 'failed') return 'badge-error'
      if (s === 'processing') return 'badge-warning'
      return 'badge-idle'
    },
    fileStatusClass() {
      const s = this.slate?.file_object?.status
      if (s === 'success') return 'badge-live'
      if (s === 'failed') return 'badge-error'
      if (['uploading', 'processing'].includes(s)) return 'badge-warning'
      return 'badge-idle'
    }
  },

  async mounted() {
    this.slateId = this.$route.params.id
    await this.loadSlate()
  },

  methods: {
    async loadSlate() {
      try {
        const res = await api.get(`/slates/${this.slateId}`)
        this.slate = res.data?.slate || res.data || null
        this.form.title = this.slate?.title || ''
      } catch (err) {
        notifyError(err?.message || 'Failed to load slate')
      }
    },

    async save() {
      if (!this.form.title?.trim()) {
        notifyError('Name is required')
        return
      }
      this.saving = true
      try {
        await api.put(`/slates/${this.slateId}`, {
          slate: { title: this.form.title }
        })
        notifySuccess('Slate updated')
        await this.loadSlate()
      } catch (err) {
        notifyError(err?.message || 'Failed to update slate')
      } finally {
        this.saving = false
      }
    },

    async copyToClipboard(text, label = 'URL') {
      if (!text) return
      try {
        await navigator.clipboard.writeText(text)
        notifySuccess(`${label} copied!`)
      } catch {
        notifyError('Failed to copy to clipboard')
      }
    }
  }
}
</script>
